import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { apiClient } from '../../../services/api';
import { Project } from '../../../types/domain';

export function useProjectSettings() {
  const navigate = useNavigate();
  const {
    user,
    role,
    currentOrg,
    currentProject,
    setCurrentProject,
    refreshProjects,
    canAdmin,
  } = useAuth();
  const { showToast } = useToast();

  const canDeleteProject = Boolean(user?.is_superadmin || role === 'owner' || role === 'admin');
  const [activeTab, setActiveTab] = useState<'general' | 'ingress' | 'danger'>('general');

  useEffect(() => {
    if (activeTab === 'danger' && !canDeleteProject) {
      setActiveTab('general');
    }
  }, [activeTab, canDeleteProject]);

  // Project General State
  const [projectName, setProjectName] = useState(currentProject?.name || '');
  const [projectDesc, setProjectDesc] = useState(currentProject?.description || '');
  const [isSavingGeneral, setIsSavingGeneral] = useState(false);

  // Ingress & Security State
  const [origins, setOrigins] = useState<string>(
    currentProject?.allowed_origins?.join(', ') || '*'
  );
  const [redirects, setRedirects] = useState<string>(
    currentProject?.allowed_redirect_urls?.join(', ') || 'https://ericius.app/success'
  );
  const [rateLimit, setRateLimit] = useState<number>(
    currentProject?.rate_limit_per_minute || 1200
  );
  const [isSavingIngress, setIsSavingIngress] = useState(false);

  // Token Copy State
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedProjectId, setCopiedProjectId] = useState(false);

  // Delete Project State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (currentProject) {
      setProjectName(currentProject.name);
      setProjectDesc(currentProject.description || '');
      setOrigins(currentProject.allowed_origins?.join(', ') || '*');
      setRedirects(
        currentProject.allowed_redirect_urls?.join(', ') || 'https://ericius.app/success'
      );
      setRateLimit(currentProject.rate_limit_per_minute || 1200);
    }
  }, [currentProject]);

  const handleSaveGeneral = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdmin || !currentProject?.id || !projectName.trim()) return;

    setIsSavingGeneral(true);
    try {
      const updated = await apiClient<Project>(`/projects/${currentProject.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: projectName.trim(),
          description: projectDesc.trim(),
        }),
      });

      if (updated) {
        localStorage.setItem('ericius_active_project_data', JSON.stringify(updated));
        setCurrentProject(updated);
      }
      await refreshProjects();

      showToast({
        type: 'success',
        title: 'Project Settings Saved',
        message: 'Project name and description updated.',
      });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Save Project',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsSavingGeneral(false);
    }
  };

  const handleSaveIngress = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdmin || !currentProject?.id) return;

    setIsSavingIngress(true);
    try {
      const originsArray = origins
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const redirectsArray = redirects
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      const updated = await apiClient<Project>(`/projects/${currentProject.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          allowed_origins: originsArray,
          allowed_redirect_urls: redirectsArray,
          rate_limit_per_minute: Number(rateLimit) || 600,
        }),
      });

      if (updated) {
        localStorage.setItem('ericius_active_project_data', JSON.stringify(updated));
        setCurrentProject(updated);
      }

      showToast({
        type: 'success',
        title: 'Ingress & Security Saved',
        message: 'Project CORS origins, redirect whitelist, and rate limits updated.',
      });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Save Ingress Settings',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsSavingIngress(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!canDeleteProject || !currentProject?.id || deleteConfirmText !== currentProject.slug) return;

    setIsDeleting(true);
    try {
      await apiClient(`/projects/${currentProject.id}`, {
        method: 'DELETE',
      });
      showToast({
        type: 'success',
        title: 'Project Deleted',
        message: `Project ${currentProject.name} was removed.`,
      });
      setDeleteModalOpen(false);
      await refreshProjects();
      navigate('/organization');
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Delete Project',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const copyProjectId = () => {
    if (currentProject?.id) {
      navigator.clipboard.writeText(currentProject.id);
      setCopiedProjectId(true);
      setTimeout(() => setCopiedProjectId(false), 2000);
      showToast({
        type: 'info',
        title: 'Copied',
        message: 'Project UUID copied to clipboard.',
      });
    }
  };

  const copyApiToken = () => {
    const token = `${currentProject?.api_token_prefix || 'erc_live'}_sample_key`;
    navigator.clipboard.writeText(token);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
    showToast({
      type: 'info',
      title: 'Copied',
      message: 'Project API Token prefix copied to clipboard.',
    });
  };

  return {
    user,
    role,
    currentOrg,
    currentProject,
    canAdmin,
    canDeleteProject,
    activeTab,
    setActiveTab,
    projectName,
    setProjectName,
    projectDesc,
    setProjectDesc,
    isSavingGeneral,
    handleSaveGeneral,
    origins,
    setOrigins,
    redirects,
    setRedirects,
    rateLimit,
    setRateLimit,
    isSavingIngress,
    handleSaveIngress,
    copiedToken,
    copiedProjectId,
    copyProjectId,
    copyApiToken,
    deleteModalOpen,
    setDeleteModalOpen,
    deleteConfirmText,
    setDeleteConfirmText,
    isDeleting,
    handleDeleteProject,
  };
}
